import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FormTooltip } from "@/components/ui/form-description";

interface DateTimeFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends React.HTMLAttributes<HTMLDivElement> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  description?: string;
  descriptionAsTooltip?: boolean;
  required?: boolean;
  requiredLabel?: string;
  visuallyHiddenLabel?: boolean;
  datePlaceholder?: string;
  timePlaceholder?: string;
  disabled?: (date: Date) => boolean;
}

export function DateTimeField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  descriptionAsTooltip = false,
  required = false,
  requiredLabel = "Required",
  visuallyHiddenLabel = false,
  datePlaceholder = "Pick a date",
  timePlaceholder = "12:00",
  disabled,
  className,
  ...htmlProps
}: DateTimeFieldProps<TFieldValues, TName>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const dateValue = field.value?.date;
        const timeValue = field.value?.time;

        const handleDateChange = (date: Date | undefined) => {
          field.onChange({
            date: date,
            time: timeValue || "",
          });
        };

        const handleTimeChange = (
          event: React.ChangeEvent<HTMLInputElement>,
        ) => {
          field.onChange({
            date: dateValue,
            time: event.target.value,
          });
        };

        return (
          <FormItem className={cn("flex flex-col", className)} {...htmlProps}>
            <div className={cn(visuallyHiddenLabel ? "sr-only" : "")}>
              <FormLabel className="inline-block leading-snug">
                {label}
                {required && (
                  <>
                    <span className="ms-1 text-red-500" aria-hidden="true">
                      *
                    </span>
                    <span className="sr-only">({requiredLabel})</span>
                  </>
                )}
              </FormLabel>
              {description && !descriptionAsTooltip && (
                <FormDescription>{description}</FormDescription>
              )}
              {description && descriptionAsTooltip && (
                <FormTooltip description={description} />
              )}
            </div>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className={cn(
                        "flex-1 bg-transparent pl-3 text-left font-normal hover:bg-transparent dark:bg-transparent",
                        !dateValue && "text-muted-foreground",
                      )}
                    >
                      {dateValue ? (
                        format(dateValue, "PPP")
                      ) : (
                        <span>{datePlaceholder}</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateValue}
                    onSelect={handleDateChange}
                    disabled={disabled}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <FormControl>
                <Input
                  type="time"
                  aria-required={required ? "true" : "false"}
                  placeholder={timePlaceholder}
                  value={timeValue || ""}
                  onChange={handleTimeChange}
                  className="w-[140px]"
                />
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
